/**
 * GoalScorePublishConsent — PR-7a, the owner's one-line leaderboard consent.
 *
 * Placed directly under the hero (Form ring + Goal Score section), per
 * F §3 / `docs/goal_score/REPORT.md` §5.6: "shown on the evaluation results
 * screen, once, directly under the Total/Goals/Form hero." Default **No**,
 * reversible — publishing or hiding never deletes the evaluation.
 *
 * Fetches `GET /evaluations/{id}/publish-status` on mount so the control
 * never silently fails to appear (the same reason the backend route exists —
 * `docs/API_BACKEND.md` §9.8). Renders NOTHING while loading-with-no-prior-
 * data and NOTHING on a load error that resolves to "not found"/"forbidden"
 * (403/404) — those mean this viewer has no publish/read standing on this
 * evaluation, which the backend itself decides; the control should not
 * render if the backend wouldn't let this viewer touch it. Any other error
 * renders a small inline retry.
 *
 * Athlete-only per F §3 ("a coach cannot publish on an athlete's behalf") —
 * the backend ACL (`_eval_publish_permission`) already enforces this for the
 * WRITE call; this component additionally does not render AT ALL for a
 * viewer the backend's `GET .../publish-status` 403s for (same ACL, read
 * side), so a coach who is merely a `shared_with_user_id` grantee never sees
 * a control they could not use anyway.
 */
import { useCallback, useEffect, useState } from 'react';
import { CameraIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import {
  extractPublishConsentErrorMessage,
  getPublishStatus,
  setPublishConsent,
} from '@/services/goalScoreApi';
import type { PublishStatusResponse } from '@/types/goalScore';
import {
  consentDefaultNote,
  consentDescription,
  consentDisabledCopy,
  publishedHelperCopy,
  publishedVisibleCopy,
} from '@/utils/goalScoreCopy';

export interface GoalScorePublishConsentProps {
  evaluationId: string;
  /** The owner's consent sentence says "Other athletes in {channel name}" — the channel this evaluation's board lives on. */
  channelName?: string | null;
  /**
   * Whether the viewer has opted the CHANNEL in at all
   * (`ChannelMember.leaderboard_opt_in`) — the independent kill switch this
   * control's own toggle is gated on top of. When `false`, the toggle
   * renders disabled with the "turn this on first" copy rather than being
   * hidden (F §3 / REPORT §5.6 — an athlete who's never seen the feature
   * should still be able to discover it).
   */
  channelOptedIn?: boolean;
  className?: string;
}

export default function GoalScorePublishConsent({
  evaluationId,
  channelName,
  channelOptedIn = true,
  className,
}: GoalScorePublishConsentProps) {
  const [status, setStatus] = useState<PublishStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<'hidden' | 'retryable' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastNotice, setLastNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const data = await getPublishStatus(evaluationId);
      setStatus(data);
    } catch (err) {
      const httpStatus = (err as { response?: { status?: number } } | undefined)?.response?.status;
      setLoadError(httpStatus === 403 || httpStatus === 404 ? 'hidden' : 'retryable');
    }
  }, [evaluationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadError === 'hidden') return null;
  if (loadError === 'retryable') {
    return (
      <div
        data-testid="goal-score-consent-error"
        className={cn('rounded-lg border border-border-subtle bg-dark-elevated-1 p-4 text-sm text-text-secondary', className)}
      >
        <p>We couldn&apos;t load your leaderboard setting right now.</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-2 text-accent text-sm font-medium"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!status) return null; // loading — render nothing rather than a layout-shifting skeleton

  // The backend's publish-status is the source of truth for both facts
  // (PR-7a red-team finding 1); the props remain as overrides for callers
  // that already know them.
  const optedIn = status.channel_opted_in ?? channelOptedIn;

  const handleChoice = async (publish: boolean) => {
    if (!optedIn || isSaving) return;
    setSaveError(null);
    setIsSaving(true);
    // Optimistic UI: reflect the choice immediately, roll back on error.
    const previous = status;
    setStatus({
      ...status,
      publish_state: publish
        ? 'published'
        : status.publish_state === 'published'
          ? 'hidden'
          : status.publish_state === 'awaiting_athlete'
            ? 'private' // the athlete's "No" to a coach-submitted eval — never a tombstone
            : status.publish_state,
    });
    try {
      const result = await setPublishConsent(evaluationId, publish);
      setStatus((current) =>
        current
          ? { ...current, publish_state: result.publish_state, published_at: result.published_at }
          : current,
      );
      setLastNotice(result.notice);
    } catch (err) {
      setStatus(previous); // rollback
      setSaveError(extractPublishConsentErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const channel = status.channel_name ?? channelName ?? 'your channel';
  const isPublished = status.publish_state === 'published';
  // Goal Score go-live B1 — athlete-owned consent: a coach who submitted
  // this evaluation on the athlete's behalf can SEE this control but may
  // never answer it (the backend 403s the write). Gate every interactive
  // element on `can_answer` so a coach is never shown a button that fails.
  // Goal Score go-live B5c (red-team finding F6): an older backend build
  // (deploy skew) may omit `can_answer` entirely. Fail OPEN to the subject
  // — treat a MISSING value as "may answer", never lock the athlete out of
  // their own consent decision because of a field a newer backend added.
  const canAnswer = status.can_answer ?? true;

  if (!canAnswer && !isPublished) {
    // A coach who submitted this evaluation may see its leaderboard state
    // but never answer the Yes/No — read-only for every non-published
    // state, not just 'awaiting_athlete' (e.g. the athlete later hid a
    // score they had published).
    const notice =
      status.publish_state === 'awaiting_athlete'
        ? status.awaiting_athlete_notice ?? 'Waiting for the athlete to decide.'
        : status.publish_state === 'hidden'
          ? 'The athlete has hidden this score from the leaderboard.'
          : 'The athlete has not published this score to the leaderboard.';
    return (
      <div
        data-testid="goal-score-publish-consent"
        className={cn('rounded-lg border border-border-subtle bg-dark-elevated-1 p-4 space-y-2', className)}
      >
        <p data-testid="goal-score-consent-awaiting-athlete-notice" className="text-sm text-text-secondary">
          {notice}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="goal-score-publish-consent"
      className={cn('rounded-lg border border-border-subtle bg-dark-elevated-1 p-4 space-y-3', className)}
    >
      {/* Filming tip — shown near publish, every time (red-team amendment 18). */}
      <div className="flex items-start gap-2 text-xs text-text-muted">
        <CameraIcon className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p>
          <strong className="text-text-secondary">Goal scores are sensitive to how you film.</strong>{' '}
          Use the same camera spot, angle, and distance as your coach&apos;s demo each time, so your
          Goal scores stay comparable session to session.
        </p>
      </div>

      {isPublished ? (
        <div data-testid="goal-score-consent-published" className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-xs font-semibold text-success">
              <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Published
            </span>
            {canAnswer && (
              <button
                type="button"
                data-testid="goal-score-consent-hide"
                onClick={() => void handleChoice(false)}
                disabled={isSaving}
                className="text-xs font-medium text-text-muted hover:text-text-primary disabled:opacity-50"
              >
                Hide this score
              </button>
            )}
          </div>
          <p className="text-xs text-text-secondary">{publishedVisibleCopy(channel)}</p>
          <p className="text-xs text-text-muted">{lastNotice ?? publishedHelperCopy()}</p>
        </div>
      ) : (
        <div data-testid="goal-score-consent-prompt" className="space-y-2">
          <p
            className={cn('text-sm font-semibold', optedIn ? 'text-text-primary' : 'text-text-muted')}
          >
            {status.consent_prompt}
          </p>
          <p className="text-xs text-text-secondary">
            {optedIn ? consentDescription(channel) : consentDisabledCopy()}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="goal-score-consent-yes"
              onClick={() => void handleChoice(true)}
              disabled={!optedIn || isSaving}
              className="rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-dark-base disabled:opacity-40"
            >
              Yes
            </button>
            <button
              type="button"
              data-testid="goal-score-consent-no"
              onClick={() => void handleChoice(false)}
              disabled={!optedIn || isSaving}
              aria-pressed={status.publish_state !== 'published'}
              className="rounded-md border border-border-subtle px-4 py-1.5 text-sm font-semibold text-text-primary disabled:opacity-40"
            >
              No
            </button>
          </div>
          {optedIn && !lastNotice && <p className="text-xs text-text-muted">{consentDefaultNote()}</p>}
          {/* The hide-notice (J §5.3) is shown once, right after a genuine
              published -> hidden transition. The UI has already switched
              back to this (not-published) branch by the time the response
              comes back, so it must render HERE, not only in the
              `isPublished` branch above — otherwise the one sentence the
              owner's ruling requires ("what's kept when hidden") would
              never actually be visible to the athlete who just saw it
              happen. Never shown for a plain "never published" state
              (`lastNotice` is only ever set by a real hide response). */}
          {status.publish_state === 'hidden' && lastNotice && (
            <p data-testid="goal-score-consent-hide-notice" className="text-xs text-text-muted">
              {lastNotice}
            </p>
          )}
        </div>
      )}

      {status.eligibility.eligible === false && status.eligibility.reason && (
        <p data-testid="goal-score-consent-ineligible" className="text-xs text-warning">
          {status.eligibility.reason}
        </p>
      )}

      {saveError && (
        <p data-testid="goal-score-consent-save-error" className="text-xs text-danger">
          {saveError}
        </p>
      )}
    </div>
  );
}
